import { useMemo, useState } from "react"
import { Warehouse } from "lucide-react"
import {
  useGetItemStockListQuery,
  useGetItemsQuery,
  useGetItemGroupsQuery,
} from "shared/redux/endpoints/procurementItems"
import { useGetActiveBudgetCategoriesQuery } from "shared/redux/endpoints/procurementV2"
import { formatDateTime } from "../../constants"
import { Card, DataTable, EmptyState, Toolbar, Select } from "../../ui"

// §2.4 Item Stock — read-only view of current on-hand quantity per item ×
// center. No ledger UI here (item_stock_transaction is out of scope).
// Book demand lives on its own page at /procurement/book-inventory; this tab
// stays the stock snapshot other screens already depend on.
const ItemStockView = () => {
  const [filters, setFilters] = useState({ itemId: "", itemGroupId: "", categoryId: "", centerId: "" })
  const set = (k, v) => setFilters((p) => ({ ...p, [k]: v }))

  const { data: items } = useGetItemsQuery({ size: 10000 })
  const { data: itemGroups } = useGetItemGroupsQuery()
  const { data: categories } = useGetActiveBudgetCategoriesQuery({ isActive: true })
  const { data: stock, isFetching } = useGetItemStockListQuery({
    itemId: filters.itemId || undefined,
    itemGroupId: filters.itemGroupId || undefined,
    categoryId: filters.categoryId || undefined,
    centerId: filters.centerId || undefined,
  })
  // Baseline unfiltered fetch (separate cache entry) just to populate the
  // Center dropdown — there's no dedicated centers-list endpoint yet, so we
  // derive { value: center.id, label: center.centerName } from whatever
  // centers show up across the full stock snapshot.
  const { data: allStock } = useGetItemStockListQuery({})

  const itemOptions = useMemo(
    () => (items || []).map((it) => ({ value: it.id, label: it.name || it.itemCode })),
    [items]
  )

  const itemGroupOptions = useMemo(
    () => (itemGroups || []).map((g) => ({ value: g.id, label: g.name || g.code })),
    [itemGroups]
  )

  const categoryOptions = useMemo(
    () => (categories || []).map((c) => ({ value: c.id, label: c.name })),
    [categories]
  )

  const centerOptions = useMemo(() => {
    const seen = new Map()
    ;(allStock || []).forEach((r) => {
      if (r.center?.id != null && !seen.has(r.center.id)) {
        seen.set(r.center.id, r.center.centerName || `Center ${r.center.id}`)
      }
    })
    return Array.from(seen, ([value, label]) => ({ value, label }))
  }, [allStock])

  const columns = useMemo(
    () => [
      {
        key: "itemName",
        header: "Item",
        strong: true,
        value: (r) => r.item?.name || r.item?.itemCode,
        render: (r) => r.item?.name || r.item?.itemCode || "—",
      },
      {
        key: "centerName",
        header: "Center",
        value: (r) => r.center?.centerName,
        render: (r) => r.center?.centerName || "—",
      },
      {
        key: "quantityOnHand",
        header: "Quantity on hand",
        align: "right",
        sortable: true,
        value: (r) => r.quantityOnHand,
        render: (r) => (r.quantityOnHand ?? "—"),
      },
      {
        key: "updatedAt",
        header: "Updated at",
        sortable: true,
        render: (r) => formatDateTime(r.updatedAt),
      },
    ],
    []
  )

  return (
    <>
      <Toolbar>
        <div className="pc-tb-field">
          <Select
            label="Item"
            placeholder="All items"
            value={filters.itemId}
            onChange={(v) => set("itemId", v)}
            options={itemOptions}
          />
        </div>
        <div className="pc-tb-field">
          <Select
            label="Item Group"
            placeholder="All item groups"
            value={filters.itemGroupId}
            onChange={(v) => set("itemGroupId", v)}
            options={itemGroupOptions}
          />
        </div>
        <div className="pc-tb-field">
          <Select
            label="Category"
            placeholder="All categories"
            value={filters.categoryId}
            onChange={(v) => set("categoryId", v)}
            options={categoryOptions}
          />
        </div>
        <div className="pc-tb-field">
          <Select
            label="Center"
            placeholder="All centers"
            value={filters.centerId}
            onChange={(v) => set("centerId", v)}
            options={centerOptions}
          />
        </div>
      </Toolbar>

      <Card flush>
        <DataTable
          columns={columns}
          rows={stock || []}
          isLoading={isFetching}
          rowKey={(r, i) => `${r.item?.id}-${r.center?.id}-${i}`}
          initialSort={{ key: "itemName", dir: "asc" }}
          empty={
            <EmptyState icon={<Warehouse aria-hidden />} title="No stock records" text="Nothing to show for this filter." />
          }
        />
      </Card>
    </>
  )
}

export default ItemStockView
